import { FinancialUnavailable, isCurrentAssessment, type FinancialResponseAvailability } from "../financial-unavailable";
import { DealFinancialEntryLinks } from "../deal-financial-entry-links";
/**
 * Calculations Summary Panel (SPRINT-15 F009, enhanced SPRINT-25 F010)
 *
 * Audit trail for DSCR calculation showing every income and commitment
 * source with provenance links, trust grade badges, and a DSCR result
 * breakdown card at the bottom.
 *
 * Sprint 25 F010 additions:
 * - Formula Derivation Tree: collapsible Adjusted EBITDA decomposition
 *   (Entity EBITDA → Eliminations → Guarantor → Adjustments)
 * - Each leaf node: entity name, period, trust grade badge, data source badge
 * - Click-through links: entity → /accounts/:id/financials, guarantor → /contacts/:id
 *
 * Props: { dealId: string }
 *
 * States:
 * - Loading: Skeleton placeholders
 * - Empty: No data message
 * - Active: Full audit trail with DSCR result card + derivation tree
 */

import React, { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  ArrowRight,
  Calculator,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { useLocation } from "wouter";
import { cn } from "@/lib/utils";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface IncomeSource {
  id: string;
  dealId: string;
  sourceType: string;
  sourceEntityId: string | null;
  sourceLabel: string;
  amount: number;
  includeInCalculation: boolean;
  periodLabel: string | null;
  notes: string | null;
  accountId?: string | null;
  contactId?: string | null;
  trustGrade?: string | null;
}

interface CommitmentItem {
  id: string;
  dealId: string;
  commitmentId: string | null;
  commitmentLabel: string;
  entityName: string | null;
  lenderName: string | null;
  annualAmount: number;
  overrideAmount: number | null;
  includeInCalculation: boolean;
  isProposed: boolean;
  notes: string | null;
  accountId?: string | null;
  trustGrade?: string | null;
}

interface ConsolidatedSnapshot extends FinancialResponseAvailability {
  id: string;
  dealId: string;
  periodLabel: string;
  snapshotId?: string;
  totalEntityEbitda?: number | null;
  totalEliminations?: number | null;
  totalGuarantorIncome?: number | null;
  totalAdjustments?: number | null;
  adjustedEbitda?: number | null;
  totalIncome?: number | null;
  totalCommitments?: number | null;
  dscr?: number | null;
  surplus?: number | null;
  passesServiceability?: boolean | null;
  entityBreakdown?: EntityBreakdown[] | null;
  consolidated?: {
    totalIncome: number;
    totalCommitments: number;
    surplus: number;
    dscr: number;
    icr: number;
    passesServiceability: boolean;
  };
}

interface EntityBreakdown {
  accountId: string;
  accountName: string;
  ebitda: number;
  periodLabel?: string;
  dataSourceType?: string;
  trustGrade?: string;
  isAnnualised?: boolean;
}

interface EliminationItem {
  id: string;
  dealId: string;
  fromAccountId: string;
  toAccountId: string;
  fromAccountName?: string | null;
  toAccountName?: string | null;
  description: string | null;
  annualAmount: number;
  periodLabel: string | null;
}

interface GuarantorItem {
  id: string;
  dealId: string;
  contactId: string;
  contactName?: string;
  treatmentType?: string;
  annualIncome?: number;
}

interface AdjustmentItem {
  id: string;
  dealId: string;
  adjustmentType: string;
  description: string | null;
  direction: string;
  amount: string | number;
  periodLabel: string | null;
}

export interface CalculationsSummaryProps {
  dealId: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function formatCurrency(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "Not assessed";
  return new Intl.NumberFormat("en-AU", {
    style: "currency",
    currency: "AUD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/** Human-readable source type label */
const SOURCE_TYPE_LABELS: Record<string, string> = {
  entity_ebitda: "Entity EBITDA",
  guarantor_income: "Guarantor Income",
  rental_income: "Rental Income",
  intercompany: "Intercompany",
  adjustment: "Adjustment",
  other: "Other",
};

function getSourceTypeLabel(sourceType: string): string {
  return SOURCE_TYPE_LABELS[sourceType] ?? sourceType;
}

/** Human-readable data source type label */
const DATA_SOURCE_LABELS: Record<string, string> = {
  accountant_prepared: "Accountant Prepared",
  tax_return: "Tax Return",
  management_accounts: "Management Accounts",
  xero: "Xero",
};

function getDataSourceLabel(dataSourceType: string | undefined): string {
  if (!dataSourceType) return "Unknown";
  return DATA_SOURCE_LABELS[dataSourceType] ?? dataSourceType;
}

function DataSourceBadge({ dataSourceType }: { dataSourceType: string | undefined }) {
  if (!dataSourceType) return null;
  return (
    <Badge
      variant="outline"
      className="text-[10px] py-0 px-1 bg-blue-50 text-blue-700 border-blue-200"
      data-testid="data-source-badge"
    >
      {getDataSourceLabel(dataSourceType)}
    </Badge>
  );
}

function TrustGradeBadge({ grade }: { grade: string | null | undefined }) {
  if (!grade) return null;
  if (grade === "credit_grade") {
    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 px-1 bg-green-50 text-green-700 border-green-200"
        data-testid="trust-grade-badge"
      >
        Credit
      </Badge>
    );
  }
  if (grade === "insight_grade") {
    return (
      <Badge
        variant="outline"
        className="text-[10px] py-0 px-1 bg-amber-50 text-amber-700 border-amber-200"
        data-testid="trust-grade-badge"
      >
        Insight
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-[10px] py-0 px-1" data-testid="trust-grade-badge">
      {grade}
    </Badge>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const CalculationsSummary: React.FC<CalculationsSummaryProps> = ({
  dealId,
}) => {
  const [, setLocation] = useLocation();

  // Derivation tree category open/closed state
  const [openCategories, setOpenCategories] = useState<Record<string, boolean>>({
    "entity-ebitda": false,
    eliminations: false,
    guarantors: false,
    adjustments: false,
  });

  const toggleCategory = (key: string) => {
    setOpenCategories((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  // -----------------------------------------------------------------------
  // Queries
  // -----------------------------------------------------------------------

  const {
    data: incomeSources = [],
    isLoading: incomeLoading,
  } = useQuery<IncomeSource[]>({
    queryKey: [`/api/financial/deals/${dealId}/income-inclusion`],
    enabled: !!dealId,
  });

  const {
    data: commitments = [],
    isLoading: commitmentsLoading,
  } = useQuery<CommitmentItem[]>({
    queryKey: [`/api/financial/deals/${dealId}/commitment-inclusion`],
    enabled: !!dealId,
  });

  const {
    data: snapshots = [],
    isLoading: snapshotsLoading,
    isError: snapshotsError,
  } = useQuery<ConsolidatedSnapshot[]>({
    queryKey: [`/api/financial/deals/${dealId}/consolidated-snapshots`],
    enabled: !!dealId,
  });

  const {
    data: eliminations = [],
  } = useQuery<EliminationItem[]>({
    queryKey: [`/api/financial/deals/${dealId}/intercompany-eliminations`],
    enabled: !!dealId,
  });

  const {
    data: guarantors = [],
  } = useQuery<GuarantorItem[]>({
    queryKey: [`/api/financial/deals/${dealId}/analysis-guarantors`],
    enabled: !!dealId,
  });

  const {
    data: ebitdaAdjustments = [],
  } = useQuery<AdjustmentItem[]>({
    queryKey: [`/api/deal-analysis/deals/${dealId}/ebitda-adjustments`],
    enabled: !!dealId,
  });

  // -----------------------------------------------------------------------
  // Derived values
  // -----------------------------------------------------------------------

  const isLoading = incomeLoading || commitmentsLoading || snapshotsLoading;

  // Use latest snapshot if available
  const latestSnapshot = useMemo(() => {
    if (snapshots.length === 0) return null;
    return isCurrentAssessment(snapshots[0]) ? snapshots[0] : null;
  }, [snapshots]);

  // Calculate totals from inclusion records
  const totalIncome = useMemo(
    () => incomeSources.some(s => s.includeInCalculation) ?
      incomeSources
        .filter((s) => s.includeInCalculation)
        .reduce((sum, s) => sum + Number(s.amount), 0) : null,
    [incomeSources]
  );

  const totalCommitments = useMemo(
    () => commitments.some(c => c.includeInCalculation) ?
      commitments
        .filter((c) => c.includeInCalculation)
        .reduce(
          (sum, c) =>
            sum +
            (c.overrideAmount != null
              ? Number(c.overrideAmount)
              : Number(c.annualAmount)),
          0
        ) : null,
    [commitments]
  );

  // Derivation tree: use snapshot entity breakdown if available
  const entityBreakdownForTree: EntityBreakdown[] = useMemo(() => {
    const breakdown = latestSnapshot?.entityBreakdown;
    if (breakdown && Array.isArray(breakdown) && breakdown.length > 0) {
      return breakdown;
    }
    return [];
  }, [latestSnapshot]);

  // -----------------------------------------------------------------------
  // Navigation helpers
  // -----------------------------------------------------------------------

  const navigateToEntity = (accountId: string | null | undefined) => {
    if (accountId) {
      setLocation(`/accounts/${accountId}/financials`);
    }
  };

  const navigateToContact = (contactId: string | null | undefined) => {
    if (contactId) {
      setLocation(`/contacts/${contactId}`);
    }
  };

  const navigateToCommitments = (accountId: string | null | undefined) => {
    if (accountId) {
      setLocation(`/accounts/${accountId}/commitments`);
    }
  };

  // -----------------------------------------------------------------------
  // Loading state
  // -----------------------------------------------------------------------

  if (isLoading) {
    return (
      <Card data-testid="calculations-summary-panel">
        <CardHeader className="pb-3">
          <Skeleton className="h-5 w-56" />
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-4 w-36" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
          <Skeleton className="h-32 w-full" />
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Empty state
  // -----------------------------------------------------------------------

  const hasData = incomeSources.length > 0 || commitments.length > 0;

  if (!hasData) {
    return (
      <Card data-testid="calculations-summary-panel">
        <CardHeader className="pb-3">
          <div className="flex items-center gap-2">
            <Calculator className="h-4 w-4 text-muted-foreground" />
            <CardTitle className="text-sm font-semibold">
              Calculations Audit Trail
            </CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Calculator className="h-8 w-8 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground">
              Not assessed. No income or commitment data available for this deal
            </p>
            <p className="text-xs text-muted-foreground mt-1">
              Configure income sources and commitments in the Source Selector to see the audit trail
            </p>
            <DealFinancialEntryLinks dealId={dealId} />
          </div>
        </CardContent>
      </Card>
    );
  }

  // -----------------------------------------------------------------------
  // Render
  // -----------------------------------------------------------------------

  return (
    <Card data-testid="calculations-summary-panel">
      <CardHeader className="pb-3">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 text-muted-foreground" />
          <CardTitle className="text-sm font-semibold">
            Calculations Audit Trail
          </CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* ─── Income Sources Audit ─── */}
        <div data-testid="income-audit-section">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            INCOME SOURCES
          </h4>

          {incomeSources.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No income sources configured
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground w-8" />
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Source
                    </th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Entity
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Period
                    </th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground w-8" />
                  </tr>
                </thead>
                <tbody>
                  {incomeSources.map((source) => {
                    const linkTarget =
                      source.sourceType === "guarantor_income"
                        ? source.contactId
                        : source.accountId;
                    const handleNavigate =
                      source.sourceType === "guarantor_income"
                        ? () => navigateToContact(source.contactId)
                        : () => navigateToEntity(source.accountId);

                    return (
                      <tr
                        key={source.id}
                        className={cn(
                          "border-b transition-opacity",
                          !source.includeInCalculation && "opacity-50"
                        )}
                      >
                        <td className="py-2 px-2">
                          <Checkbox
                            checked={source.includeInCalculation}
                            disabled
                            aria-label={`${source.sourceLabel} included`}
                          />
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2",
                            !source.includeInCalculation && "line-through"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span>{source.sourceLabel || getSourceTypeLabel(source.sourceType)}</span>
                            <Badge variant="outline" className="text-[10px]">
                              {getSourceTypeLabel(source.sourceType)}
                            </Badge>
                            <TrustGradeBadge grade={source.trustGrade} />
                          </div>
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-right font-mono",
                            !source.includeInCalculation && "line-through",
                            source.amount < 0 ? "text-red-600" : "text-foreground"
                          )}
                        >
                          {formatCurrency(source.amount)}
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-muted-foreground",
                            !source.includeInCalculation && "line-through"
                          )}
                        >
                          {source.sourceEntityId ?? "\u2014"}
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-muted-foreground",
                            !source.includeInCalculation && "line-through"
                          )}
                        >
                          {source.periodLabel ?? "\u2014"}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {linkTarget && (
                            <button
                              type="button"
                              onClick={handleNavigate}
                              className="inline-flex items-center justify-center rounded-sm p-1 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                              title="View source data"
                              data-testid={`income-link-${source.id}`}
                            >
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t">
                    <td className="py-2 px-2" colSpan={2}>
                      = TOTAL INCOME
                    </td>
                    <td className="py-2 px-2 text-right font-mono">
                      {formatCurrency(totalIncome)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ─── Debt Service Audit ─── */}
        <div data-testid="commitment-audit-section">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            DEBT SERVICE OBLIGATIONS
          </h4>

          {commitments.length === 0 ? (
            <p className="text-sm text-muted-foreground py-4 text-center">
              No commitments configured
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b">
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground w-8" />
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Commitment
                    </th>
                    <th className="text-right py-2 px-2 font-medium text-muted-foreground">
                      Amount
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Entity
                    </th>
                    <th className="text-left py-2 px-2 font-medium text-muted-foreground">
                      Lender
                    </th>
                    <th className="text-center py-2 px-2 font-medium text-muted-foreground w-8" />
                  </tr>
                </thead>
                <tbody>
                  {commitments.map((commitment) => {
                    const effectiveAmount =
                      commitment.overrideAmount != null
                        ? Number(commitment.overrideAmount)
                        : Number(commitment.annualAmount);

                    return (
                      <tr
                        key={commitment.id}
                        className={cn(
                          "border-b transition-opacity",
                          !commitment.includeInCalculation && "opacity-50",
                          commitment.isProposed && "bg-blue-50/50"
                        )}
                      >
                        <td className="py-2 px-2">
                          <Checkbox
                            checked={commitment.includeInCalculation}
                            disabled
                            aria-label={`${commitment.commitmentLabel} included`}
                          />
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2",
                            !commitment.includeInCalculation && "line-through"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <span>{commitment.commitmentLabel}</span>
                            {commitment.isProposed && (
                              <Badge variant="default" className="text-[10px]">
                                PROPOSED
                              </Badge>
                            )}
                            <TrustGradeBadge grade={commitment.trustGrade} />
                          </div>
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-right font-mono",
                            !commitment.includeInCalculation && "line-through"
                          )}
                        >
                          {formatCurrency(effectiveAmount)}
                          {commitment.overrideAmount != null && (
                            <span className="text-[10px] text-muted-foreground ml-1">
                              (override)
                            </span>
                          )}
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-muted-foreground",
                            !commitment.includeInCalculation && "line-through"
                          )}
                        >
                          {commitment.entityName ?? "\u2014"}
                        </td>
                        <td
                          className={cn(
                            "py-2 px-2 text-muted-foreground",
                            !commitment.includeInCalculation && "line-through"
                          )}
                        >
                          {commitment.lenderName ?? "\u2014"}
                        </td>
                        <td className="py-2 px-2 text-center">
                          {commitment.accountId && (
                            <button
                              type="button"
                              onClick={() =>
                                navigateToCommitments(commitment.accountId)
                              }
                              className="inline-flex items-center justify-center rounded-sm p-1 hover:bg-muted transition-colors text-muted-foreground hover:text-foreground"
                              title="View commitment data"
                              data-testid={`commitment-link-${commitment.id}`}
                            >
                              <ArrowRight className="h-3.5 w-3.5" />
                            </button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="font-semibold border-t">
                    <td className="py-2 px-2" colSpan={2}>
                      = TOTAL DEBT SERVICE
                    </td>
                    <td className="py-2 px-2 text-right font-mono">
                      {formatCurrency(totalCommitments)}
                    </td>
                    <td colSpan={3} />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>

        {/* ─── Formula Derivation Tree ─── */}
        <div data-testid="ebitda-derivation-tree">
          <h4 className="text-xs font-semibold text-muted-foreground mb-2">
            ADJUSTED EBITDA DERIVATION
          </h4>

          <div className="space-y-1 border rounded-md p-3 bg-muted/20">
            {/* Category: Entity EBITDA */}
            <Collapsible
              open={openCategories["entity-ebitda"]}
              onOpenChange={() => toggleCategory("entity-ebitda")}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-between w-full text-left py-1 px-2 rounded hover:bg-muted/40 transition-colors"
                  data-testid="derivation-category-entity-ebitda"
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {openCategories["entity-ebitda"] ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span>Entity EBITDA Total</span>
                  </div>
                  <span className="text-sm font-mono text-muted-foreground">
                    {latestSnapshot?.totalEntityEbitda != null
                      ? formatCurrency(Number(latestSnapshot.totalEntityEbitda))
                      : "—"}
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-6 mt-1 space-y-1">
                  {entityBreakdownForTree.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1 pl-2">
                      No entity breakdown available — run calculation first
                    </p>
                  ) : (
                    entityBreakdownForTree.map((entity) => (
                      <div
                        key={entity.accountId}
                        className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/30 text-sm"
                        data-testid={`derivation-leaf-${entity.accountId}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <button
                            type="button"
                            className="text-left text-primary hover:underline truncate max-w-[180px]"
                            onClick={() =>
                              setLocation(`/accounts/${entity.accountId}/financials`)
                            }
                            data-testid="entity-link"
                          >
                            {entity.accountName}
                          </button>
                          {entity.periodLabel && (
                            <span className="text-[10px] text-muted-foreground">
                              ({entity.periodLabel})
                            </span>
                          )}
                          <TrustGradeBadge grade={entity.trustGrade} />
                          <DataSourceBadge dataSourceType={entity.dataSourceType} />
                        </div>
                        <span className="font-mono text-sm text-muted-foreground ml-2 shrink-0">
                          {formatCurrency(entity.ebitda)}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Category: Intercompany Eliminations */}
            <Collapsible
              open={openCategories["eliminations"]}
              onOpenChange={() => toggleCategory("eliminations")}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-between w-full text-left py-1 px-2 rounded hover:bg-muted/40 transition-colors"
                  data-testid="derivation-category-eliminations"
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {openCategories["eliminations"] ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span>Intercompany Eliminations</span>
                  </div>
                  <span className="text-sm font-mono text-red-600">
                    {latestSnapshot?.totalEliminations != null
                      ? formatCurrency(Number(latestSnapshot.totalEliminations))
                      : "—"}
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-6 mt-1 space-y-1">
                  {eliminations.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1 pl-2">
                      No eliminations configured
                    </p>
                  ) : (
                    eliminations.map((elim) => (
                      <div
                        key={elim.id}
                        className="flex items-center justify-between py-1 px-2 rounded text-sm text-muted-foreground"
                        data-testid={`derivation-elim-${elim.id}`}
                      >
                        <span className="truncate max-w-[220px]">
                          {elim.description ?? `${elim.fromAccountName ?? ""} → ${elim.toAccountName ?? ""}`}
                        </span>
                        <span className="font-mono text-red-600 ml-2 shrink-0">
                          -{formatCurrency(Math.abs(elim.annualAmount))}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Category: Guarantor Income */}
            <Collapsible
              open={openCategories["guarantors"]}
              onOpenChange={() => toggleCategory("guarantors")}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-between w-full text-left py-1 px-2 rounded hover:bg-muted/40 transition-colors"
                  data-testid="derivation-category-guarantors"
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {openCategories["guarantors"] ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span>Guarantor Income</span>
                  </div>
                  <span className="text-sm font-mono text-muted-foreground">
                    {latestSnapshot?.totalGuarantorIncome != null
                      ? formatCurrency(Number(latestSnapshot.totalGuarantorIncome))
                      : "—"}
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-6 mt-1 space-y-1">
                  {guarantors.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1 pl-2">
                      No guarantors included
                    </p>
                  ) : (
                    guarantors.map((guar) => (
                      <div
                        key={guar.id}
                        className="flex items-center justify-between py-1 px-2 rounded hover:bg-muted/30 text-sm"
                        data-testid={`derivation-guarantor-${guar.contactId}`}
                      >
                        <button
                          type="button"
                          className="text-left text-primary hover:underline truncate max-w-[220px]"
                          onClick={() =>
                            setLocation(`/contacts/${guar.contactId}`)
                          }
                          data-testid="guarantor-link"
                        >
                          {guar.contactName ?? "Guarantor"}
                        </button>
                        <span className="font-mono text-sm text-muted-foreground ml-2 shrink-0">
                          {guar.annualIncome != null
                            ? formatCurrency(guar.annualIncome)
                            : "—"}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Category: Deal Adjustments */}
            <Collapsible
              open={openCategories["adjustments"]}
              onOpenChange={() => toggleCategory("adjustments")}
            >
              <CollapsibleTrigger asChild>
                <button
                  type="button"
                  className="flex items-center justify-between w-full text-left py-1 px-2 rounded hover:bg-muted/40 transition-colors"
                  data-testid="derivation-category-adjustments"
                >
                  <div className="flex items-center gap-2 text-sm font-medium">
                    {openCategories["adjustments"] ? (
                      <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
                    )}
                    <span>Deal Adjustments</span>
                  </div>
                  <span className="text-sm font-mono text-muted-foreground">
                    {latestSnapshot?.totalAdjustments != null
                      ? formatCurrency(Number(latestSnapshot.totalAdjustments))
                      : "—"}
                  </span>
                </button>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className="ml-6 mt-1 space-y-1">
                  {ebitdaAdjustments.length === 0 ? (
                    <p className="text-xs text-muted-foreground py-1 pl-2">
                      No deal adjustments configured
                    </p>
                  ) : (
                    ebitdaAdjustments.map((adj) => (
                      <div
                        key={adj.id}
                        className="flex items-center justify-between py-1 px-2 rounded text-sm text-muted-foreground"
                        data-testid={`derivation-adj-${adj.id}`}
                      >
                        <span className="truncate max-w-[220px]">
                          {adj.description ?? adj.adjustmentType}
                          {adj.direction === "add" ? " (Add)" : " (Deduct)"}
                        </span>
                        <span
                          className={cn(
                            "font-mono ml-2 shrink-0",
                            adj.direction === "add" ? "text-green-700" : "text-red-600"
                          )}
                        >
                          {adj.direction === "add" ? "+" : "-"}
                          {formatCurrency(Math.abs(Number(adj.amount)))}
                        </span>
                      </div>
                    ))
                  )}
                </div>
              </CollapsibleContent>
            </Collapsible>

            {/* Adjusted EBITDA total row */}
            <div className="flex items-center justify-between py-2 px-2 border-t mt-2 font-semibold">
              <span className="text-sm">= Adjusted EBITDA</span>
              <span className="font-mono text-sm">
                {latestSnapshot?.adjustedEbitda != null
                  ? formatCurrency(Number(latestSnapshot.adjustedEbitda))
                  : "—"}
              </span>
            </div>
          </div>
        </div>

        <div data-testid="dscr-result-section">
          <FinancialUnavailable dealId={dealId} value={snapshotsError ? { assessmentStatus: 'failed', historical: snapshots[0] } : snapshots[0]} action="Review financial inputs and lender policy, then use Calculate in Serviceability." />
        </div>
      </CardContent>
    </Card>
  );
};

export default CalculationsSummary;
